import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = '5dbs.admin.token.v1';

// Resolve backend-served asset paths so they work in both preview
// (ingress only forwards /api/*) and production (Nginx serves /files/).
// Absolute URLs (http/https/data) are returned as-is.
export const resolveAsset = (url) => {
  if (!url) return url;
  if (/^(https?:|data:)/i.test(url)) return url;
  if (url.startsWith('/files/')) return `${API}${url}`; // -> /api/files/...
  if (url.startsWith('/api/files/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

export const auth = {
  getToken() {
    try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  },
  setToken(t) {
    try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
  },
  isAuthed() { return !!this.getToken(); },
  async login(email, password) {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    this.setToken(data.token);
    return data;
  },
  async me() {
    return axios.get(`${API}/auth/me`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${this.getToken()}` },
    }).then(r => r.data);
  },
  async logout() {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });
    } catch {}
    this.setToken('');
  },
};

export const apiAuthed = axios.create({ baseURL: API, withCredentials: true });
apiAuthed.interceptors.request.use((cfg) => {
  const t = auth.getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
// Any 401 → drop the stale token and bounce to the admin login. Prevents
// pages hanging on "Loading…" when JWT_SECRET rotates or the token expires.
apiAuthed.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try { auth.setToken(''); } catch { /* noop */ }
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin') {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/admin?next=${next}`);
      }
    }
    return Promise.reject(err);
  }
);

export const blogApi = {
  list: (published_only = false) =>
    axios.get(`${API}/blog/posts`, { params: { published_only } }).then(r => r.data),
  get: (slug) => axios.get(`${API}/blog/posts/${encodeURIComponent(slug)}`).then(r => r.data),
  create: (payload) => apiAuthed.post('/blog/posts', payload).then(r => r.data),
  update: (slug, payload) => apiAuthed.put(`/blog/posts/${encodeURIComponent(slug)}`, payload).then(r => r.data),
  remove: (slug) => apiAuthed.delete(`/blog/posts/${encodeURIComponent(slug)}`).then(r => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiAuthed.post('/blog/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
};

// Turn "Some Title!!" into "some-title"
export const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
